import { useCallback, useEffect, useState } from 'react';
import {
  clearStoredAccessKey,
  fetchNseCaricatureBuildInfo,
  generateNseCaricature,
  nseFrameUrl,
  saveNseFrame,
} from '../utils/nseCaricatureApi';
import './NseCelebrationPage.css';

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ''}/nse-celebration/poster-template.png`;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export default function NseCelebrationPage() {
  const [guestImage, setGuestImage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [posterImage, setPosterImage] = useState('');
  const [rev, setRev] = useState('');
  const [frameStamp, setFrameStamp] = useState(() => Date.now());
  const [frameNote, setFrameNote] = useState('');

  const backgroundUrl = `${nseFrameUrl('background')}?t=${frameStamp}`;
  const alphaUrl = `${nseFrameUrl('alpha')}?t=${frameStamp}`;

  useEffect(() => {
    fetchNseCaricatureBuildInfo()
      .then((d) => setRev(d?.rev || ''))
      .catch(() => {});
  }, []);

  const onFrame = async (kind, file) => {
    if (!file) return;
    setError('');
    setFrameNote('');
    try {
      const dataUrl = await readFileAsDataUrl(file);
      await saveNseFrame(kind === 'alpha' ? { alpha: dataUrl } : { background: dataUrl });
      setFrameStamp(Date.now());
      setPosterImage('');
      setFrameNote(kind === 'alpha' ? 'Alpha mask saved.' : 'Background saved.');
    } catch (e) {
      setError(e.message || 'Could not save frame');
    }
  };
  const onPick = async (file) => {
    if (!file) return;
    setError('');
    try {
      setGuestImage(await readFileAsDataUrl(file));
      setPosterImage('');
    } catch (e) {
      setError(e.message || 'Could not read file');
    }
  };

  const onGenerate = useCallback(async () => {
    if (!guestImage) {
      setError('Choose a photo first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await generateNseCaricature({ image: guestImage });
      setPosterImage(data.image || '');
      const m = data.meta;
      if (m?.nse_celebration_rev) setRev(String(m.nse_celebration_rev));
    } catch (e) {
      setError(e.message || 'Generation failed');
      setPosterImage('');
      if (e.code === 'SAMPLE_ACCESS_DENIED') clearStoredAccessKey();
    } finally {
      setBusy(false);
    }
  }, [guestImage]);

  return (
    <div className="nse-page">
      {busy ? <div className="nse-busy" role="status">Creating your poster…</div> : null}
      <header className="nse-header">
        <div>
          <h1>NSE listing celebration poster</h1>
          <p className="nse-sub">
            Upload the poster background, the mask, and a person photo. FLUX restyles the person from one prompt, then the result is placed inside the mask on a blue circle.
          </p>
        </div>
        {rev ? <span className="nse-rev">{rev}</span> : null}
      </header>

      <div className="nse-layout">
        <section className="nse-card">
          <h2>Photo</h2>
          <label className="nse-label" htmlFor="nse-bg">Poster background</label>
          <input
            id="nse-bg"
            type="file"
            accept="image/*"
            className="nse-file"
            onChange={(e) => onFrame('background', e.target.files?.[0])}
          />
          <label className="nse-label" htmlFor="nse-alpha">Alpha mask (white = person area)</label>
          <input
            id="nse-alpha"
            type="file"
            accept="image/*"
            className="nse-file"
            onChange={(e) => onFrame('alpha', e.target.files?.[0])}
          />
          {frameNote ? <p className="nse-note">{frameNote}</p> : null}
          <img className="nse-alpha-preview" src={alphaUrl} alt="Current alpha mask" />
          <label className="nse-label" htmlFor="nse-photo">Portrait or upper-body photo</label>
          <input
            id="nse-photo"
            type="file"
            accept="image/*"
            className="nse-file"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
          <button type="button" className="nse-btn" disabled={busy} onClick={onGenerate}>
            Generate poster
          </button>
          <button
            type="button"
            className="nse-btn nse-btn-secondary"
            onClick={() => {
              clearStoredAccessKey();
              window.location.reload();
            }}
          >
            Change access key
          </button>
          {error ? <p className="nse-error" role="alert">{error}</p> : null}
        </section>

        <section className="nse-card nse-poster-wrap">
          <h2>Preview</h2>
          <div className="nse-preview-grid">
            <figure>
              <figcaption>Your upload</figcaption>
              {guestImage ? (
                <img src={guestImage} alt="Upload" />
              ) : (
                <div className="nse-placeholder">No photo yet</div>
              )}
            </figure>
            <figure>
              <figcaption>Poster</figcaption>
              {posterImage ? (
                <img src={posterImage} alt="Celebration poster" />
              ) : (
                <img src={backgroundUrl || TEMPLATE_URL} alt="Poster frame preview" />
              )}
            </figure>
          </div>
        </section>
      </div>
    </div>
  );
}
